import useChallengesQuery from "@hooks/common/challenges/use-challenges-query";
import {
  ChallengeDescription,
  ChallengeDetails,
  DialogActions,
  DialogButton,
  DialogContent,
  PlayButton,
  VideoPreview,
} from "@Components/game/challenges/bootcamp-challenge-dialog.styles";
import { BybDialog } from "@Components/game/common";
import HapticsButtonWrapper from "@Components/generic/haptics-button-wrapper";
import Icon from "@Components/generic/icon";
import { getChallengeLocizeKeyPrefix } from "@Containers/game/challenges/utils";
import { faPlay } from "@fortawesome/pro-solid-svg-icons";
import useI18n from "@i18n";
import { Typography } from "@mui/material";
import { ChallengeResponse } from "@Shared/schemas/challenges/challengeSchemas";
import { useTranslation } from "react-i18next";

interface IBootcampChallengeDialog {
  challenge: ChallengeResponse;
  open: boolean;
  onClose: () => void;
  onStart: () => void;
  onWatchVideo: (challenge: ChallengeResponse, title: string) => void;
}

const BootcampChallengeDialog = ({ challenge, open, onClose, onStart, onWatchVideo }: IBootcampChallengeDialog) => {
  const { i18n } = useI18n();
  const { t } = useTranslation("VR");
  const { challenges: challengeData = [] } = useChallengesQuery(
    {
      ids: [challenge.id],
      include_fields: ["scenarios", "targets", "rewards"],
    },
    { enabled: open }
  );

  const challengeDetails = challengeData?.[0] || challenge;
  const videoUrl = challengeDetails.video_url;
  const locizeKeyPrefix = getChallengeLocizeKeyPrefix(challengeDetails);
  const challengeTitle = String(t(`${locizeKeyPrefix}.name`, challengeDetails.title || ""));

  const handleWatchVideo = () => {
    if (videoUrl) onWatchVideo(challengeDetails, challengeTitle);
  };

  return (
    <BybDialog name="bootcamp-challenge-dialog" open={open} onClose={onClose} showCloseButton transparent width="520px">
      <DialogContent>
        <HapticsButtonWrapper>
          <VideoPreview
            type="button"
            aria-label={i18n("career.watch.video")}
            onClick={handleWatchVideo}
            $hasVideo={Boolean(videoUrl)}
            $imageUrl={challengeDetails.image_url}
          >
            {videoUrl && (
              <PlayButton>
                <Icon icon={faPlay} size="lg" />
              </PlayButton>
            )}
          </VideoPreview>
        </HapticsButtonWrapper>

        <ChallengeDetails>
          <Typography variant="textBase500" color="text.secondary">
            {i18n("label.league.bootcamp")}
          </Typography>
          <Typography variant="text3xl700" color="dsBase.v200">
            {challengeTitle}
          </Typography>
          <ChallengeDescription variant="text1xl400" color="text.secondary">
            {String(t(`${locizeKeyPrefix}.description`, challengeDetails.description || ""))}
          </ChallengeDescription>
        </ChallengeDetails>

        <DialogActions>
          <DialogButton color="tertiary" size="large" fullWidth onClick={onStart} removePadding>
            {i18n("challenges.play-challenge")}
          </DialogButton>
          <DialogButton size="large" fullWidth onClick={handleWatchVideo} disabled={!videoUrl}>
            {i18n("career.watch.video")}
          </DialogButton>
        </DialogActions>
      </DialogContent>
    </BybDialog>
  );
};

export default BootcampChallengeDialog;
